

import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Box,
  Grid,
  Switch,
  Tooltip,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Analytics,
  PlayArrow,
  Pause,
  AccountTree,
  FilterList,
  Clear,
  Timeline,
  Queue
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { alpha } from '@mui/material/styles';
import { mockObjects } from 'data/mockAutomationData';
import { format } from 'date-fns';
import { formatDate } from 'utils/DateTimeConversion';
import { useAuth } from 'auth/Auth';
import { statusChipSx, statusColors, dangerText } from 'components/AutomationWizard/styles/wizardSx';

// Helper function to capitalize first letter and make rest lowercase
const capitalizeFirst = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/** travel_orders → Travel Orders */
const formatObjectLabel = (objectId) => {
  if (!objectId) return '';
  return String(objectId)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const objectIcons = {
  Lead: 'assignment',
  Deal: 'trending_up',
  Account: 'business',
  Contact: 'person',
  Quote: 'request_quote',
  Order: 'shopping_cart',
  Invoice: 'receipt',
  Echo: 'record_voice_over',
  Travel_orders: 'flight_takeoff',
  travel_orders: 'flight_takeoff',
};

function AutomationsList({ 
  automations, 
  onEdit, 
  onViewLogs, 
  onViewFlow, 
  onToggleStatus, 
  onDelete,
  isDeleting,
  onViewAnalytics,
  onRequestDelete
}) {
  const { axiosInstance } = useAuth();
  const [orderBy, setOrderBy] = useState('updated_at');
  const [order, setOrder] = useState('desc');
  const [filterObject, setFilterObject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [automationToDelete, setAutomationToDelete] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);

  // Prefer objects that actually have automations (includes travel_orders),
  // then fall back to the static mock list for empty tenants.
  const objectFilterOptions = useMemo(() => {
    const map = new Map();
    (automations || []).forEach((a) => {
      const id = (a.trigger_object || a.object || '').trim();
      if (!id) return;
      const key = id.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { id: key, name: formatObjectLabel(id) });
      }
    });
    if (map.size === 0) {
      mockObjects.forEach((obj) => {
        const key = String(obj.id).toLowerCase();
        map.set(key, { id: key, name: obj.name });
      });
    } else {
      // Still surface known objects from mock so empty categories remain filterable
      mockObjects.forEach((obj) => {
        const key = String(obj.id).toLowerCase();
        if (!map.has(key)) {
          map.set(key, { id: key, name: obj.name });
        }
      });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [automations]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuOpen = (event, automation) => {
    setAnchorEl(event.currentTarget);
    setSelectedAutomation(automation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAutomation(null);
  };

  const handleDeleteClick = (automation) => {
    handleMenuClose();
    if (onRequestDelete) {
      onRequestDelete(automation);
      return;
    }
    // Fallback to internal dialog if parent did not provide handler
    setAutomationToDelete(automation);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (automationToDelete) {
      onDelete(automationToDelete.id);
      setDeleteDialog(false);
      setAutomationToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog(false);
    setAutomationToDelete(null);
  };

  const clearFilters = () => {
    setFilterObject('');
    setFilterStatus('');
    setSearchTerm('');
  };

  const fetchQueueStatus = async () => {
    try {
      if (!axiosInstance) return;
      const response = await axiosInstance.get('/automations/analytics/queue/status');
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Error fetching queue status:', error);
    }
  };

  useEffect(() => {
    fetchQueueStatus();
  }, [axiosInstance]);

  const filteredAndSortedAutomations = useMemo(() => {
    // Debug: Log filter values and automation data
    if (filterObject) {
      console.log('Filter Object:', filterObject);
      console.log('Automations:', automations.map(a => ({ 
        id: a.id, 
        name: a.name, 
        display_name: capitalizeFirst(a.name),
        trigger_object: a.trigger_object, 
        object: a.object,
        display_object: capitalizeFirst(a.trigger_object || a.object),
        display_trigger: capitalizeFirst(a.trigger_event || a.trigger)
      })));
    }
    
    let filtered = automations.filter(automation => {
      const matchesSearch = capitalizeFirst(automation.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        capitalizeFirst(automation.trigger_object || automation.object).toLowerCase().includes(searchTerm.toLowerCase()) ||
        capitalizeFirst(automation.trigger_event || automation.trigger).toLowerCase().includes(searchTerm.toLowerCase());
      
      // Fix: Check if trigger_object exists and handle case sensitivity
      const automationObject = automation.trigger_object || automation.object || '';
      const matchesObject = !filterObject || automationObject.toLowerCase() === filterObject.toLowerCase();
      
      const matchesStatus = !filterStatus || (automation.is_active ? 'Active' : 'Inactive') === filterStatus;
      
      return matchesSearch && matchesObject && matchesStatus;
    });

    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'created_at' || orderBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (orderBy === 'is_active') {
        // For boolean sorting, convert to numbers (true = 1, false = 0)
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else if (orderBy === 'trigger_object' || orderBy === 'trigger_event') {
        // For string sorting, use the fallback values and convert to lowercase
        aValue = (aValue || a.object || a.trigger || '').toLowerCase();
        bValue = (bValue || b.object || b.trigger || '').toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [automations, orderBy, order, filterObject, filterStatus, searchTerm]);

  const renderFilters = () => (
    <Card sx={{ 
      mb: 3, 
      p: 2.5,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'outline.variant'
    }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search automations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, object, or trigger..."
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '40px',
                borderRadius: '8px'
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Object</InputLabel>
            <Select
              value={filterObject}
              onChange={(e) => {
                console.log('Selected filter object:', e.target.value);
                setFilterObject(e.target.value);
              }}
              label="Filter by Object"
              sx={{
                height: '40px',
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 14px'
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Objects</MenuItem>
              {objectFilterOptions.map(obj => (
                <MenuItem key={obj.id} value={obj.id}>
                  {obj.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
              sx={{
                height: '40px',
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 14px'
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" gap={1} justifyContent="center">
            {/* Debug info */}
            {filterObject && (
              <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                Filter: {formatObjectLabel(filterObject)}
              </Typography>
            )}
            <Tooltip title="Clear filters">
              <IconButton 
                onClick={clearFilters} 
                color="primary"
                sx={{
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );

  const renderStatsCards = () => {
    const activeCount = automations.filter(a => a.is_active === true).length;
    const inactiveCount = automations.filter(a => a.is_active === false).length;
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'outline.variant',
            '&:hover': { borderColor: 'primary.main' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="info" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {automations.length}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
              Total Automations
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'outline.variant',
            '&:hover': { borderColor: 'primary.main' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={(theme) => ({ mb: 0.5, fontSize: '1.25rem', color: statusColors(theme, 'success').text })}
            >
              {activeCount}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
              Active
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'outline.variant',
            '&:hover': { borderColor: 'primary.main' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="text" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {inactiveCount}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
              Inactive
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'outline.variant',
            '&:hover': { borderColor: 'primary.main' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={(theme) => ({ mb: 0.5, fontSize: '1.25rem', color: statusColors(theme, 'warning').text })}
            >
              {Math.round((activeCount / automations.length) * 100) || 0}%
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
              Active Rate
            </MDTypography>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderEmptyState = () => (
    <Paper sx={{ 
      p: 6, 
      textAlign: 'center', 
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'outline.variant'
    }}>
      <AccountTree sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <MDTypography variant="h5" fontWeight="bold" gutterBottom color="text.primary">
        No automations found
      </MDTypography>
      <MDTypography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        {searchTerm || filterObject || filterStatus ? 
          'No automations match your current filters. Try adjusting your search criteria.' :
          'Get started by creating your first automation to streamline your workflows.'
        }
      </MDTypography>
      {(searchTerm || filterObject || filterStatus) && (
        <MDButton variant="outlined" onClick={clearFilters}>
          Clear Filters
        </MDButton>
      )}
    </Paper>
  );

  return (
    <MDBox>
      {renderStatsCards()}
      {renderFilters()}
      
      {filteredAndSortedAutomations.length === 0 ? (
        renderEmptyState()
      ) : (
        <Card sx={{ 
          borderRadius: 2,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'outline.variant',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableBody>
                {/* Header Row */}
                <TableRow sx={{ bgcolor: 'surface.container', '& > *': { borderBottom: '2px solid', borderColor: 'outline.variant' } }}>
                  <TableCell sx={{ 
                    width: '25%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '12%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'trigger_object'}
                      direction={orderBy === 'trigger_object' ? order : 'asc'}
                      onClick={() => handleSort('trigger_object')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Object
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '12%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'trigger_event'}
                      direction={orderBy === 'trigger_event' ? order : 'asc'}
                      onClick={() => handleSort('trigger_event')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Trigger
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '15%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'is_active'}
                      direction={orderBy === 'is_active' ? order : 'asc'}
                      onClick={() => handleSort('is_active')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '14%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'updated_by_name'}
                      direction={orderBy === 'updated_by_name' ? order : 'asc'}
                      onClick={() => handleSort('updated_by_name')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Updated By
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '11%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'created_at'}
                      direction={orderBy === 'created_at' ? order : 'asc'}
                      onClick={() => handleSort('created_at')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Created Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '11%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }}>
                    <TableSortLabel
                      active={orderBy === 'updated_at'}
                      direction={orderBy === 'updated_at' ? order : 'asc'}
                      onClick={() => handleSort('updated_at')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Modified Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '12%', 
                    fontWeight: 'bold', 
                    fontSize: '0.875rem',
                    padding: '12px 16px',
                    color: 'text.primary'
                  }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
                
                {/* Data Rows */}
                {filteredAndSortedAutomations.map((automation) => (
                  <TableRow
                    key={automation.id}
                    hover
                    sx={(theme) => ({
                      '& > *': { borderBottom: '1px solid', borderColor: 'outline.variant' },
                      '&.MuiTableRow-hover:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) }
                    })}
                  >
                    <TableCell sx={{ 
                      width: '25%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ fontSize: '0.875rem' }}>
                        {capitalizeFirst(automation.name)}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '12%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {capitalizeFirst(automation.trigger_object || automation.object)}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '12%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {capitalizeFirst(automation.trigger_event || automation.trigger)}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '15%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={automation.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={statusChipSx(automation.is_active ? 'success' : 'neutral')}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '14%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {automation.updated_by_name || automation.updated_by || ' '}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '11%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {automation.created_at ? formatDate(automation.created_at) : ' '}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '11%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {automation.updated_at ? formatDate(automation.updated_at) : ' '}
                      </MDTypography>
                    </TableCell>
                    <TableCell sx={{ 
                      width: '12%', 
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }} align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="Edit automation">
                          <IconButton 
                            size="small"
                            onClick={() => onEdit(automation)}
                            sx={{ color: 'primary.main' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View flow">
                          <IconButton 
                            size="small"
                            onClick={() => onViewFlow(automation)}
                            sx={{ color: 'success.main' }}
                          >
                            <AccountTree />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View logs">
                          <IconButton 
                            size="small"
                            onClick={() => onViewLogs(automation)}
                            sx={{ color: 'info.main' }}
                          >
                            <Analytics />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="More actions">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleMenuOpen(e, automation)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* <MenuItem onClick={() => {
          onViewAnalytics();
          handleMenuClose();
        }}>
          <Timeline sx={{ mr: 1 }} />
          View Analytics
        </MenuItem> */}
        <MenuItem onClick={() => {
          onToggleStatus(selectedAutomation.id);
          handleMenuClose();
        }}>
          {selectedAutomation?.is_active ? 
            <><Pause sx={{ mr: 1 }} />Deactivate</> : 
            <><PlayArrow sx={{ mr: 1 }} />Activate</>
          }
        </MenuItem>
        <MenuItem 
          onClick={() => handleDeleteClick(selectedAutomation)}
          sx={(theme) => ({ color: dangerText(theme) })}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog (fallback, not used when onRequestDelete is provided) */}
      {!onRequestDelete && (
        <Dialog
          open={deleteDialog}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone.
            </Alert>
            <Typography>
              Are you sure you want to delete the automation "{automationToDelete?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleDeleteCancel}>
              Cancel
            </MDButton>
            <MDButton 
              variant="gradient" 
              color="error" 
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
    </MDBox>
  );
}

export default AutomationsList; 